import { createBrowserRouter, Navigate, Outlet, useParams } from "react-router";
import { AppShell } from "@/components/layout/AppShell";
import { AuthGuard } from "@/guards/AuthGuard";
import { FeatureRouteGuard } from "@/guards/FeatureRouteGuard";
import { RoleGuard } from "@/guards/RoleGuard";
import { buildAIPhonePath } from "@/lib/ai-phone-navigation";
import { lazyWithRetryRoute } from "@/lib/lazyWithRetry";
import LoginPage from "@/routes/login";
import NotFound from "@/routes/not-found";
import PreisePage from "@/routes/preise";
import HilfeCenterPage from "@/routes/hilfe-center";
import ImpressumPage from "@/routes/impressum";
import DatenschutzPage from "@/routes/datenschutz";
import AGBPage from "@/routes/agb";
import AVVPage from "@/routes/avv";

// Landing page — lazy-loaded mit eigenem GSAP-Chunk
const landingLazy = lazyWithRetryRoute(() => import("@/routes/landing"));
const acceptInviteLazy = lazyWithRetryRoute(() => import("@/routes/accept-invite"));

// Lazy-loaded routes für Code-Splitting (mit automatischem Retry bei ChunkLoadError)
const dashboardLazy = lazyWithRetryRoute(() => import("@/routes/dashboard"));
const calendarLazy = lazyWithRetryRoute(() => import("@/routes/calendar"));
const ticketsLazy = lazyWithRetryRoute(() => import("@/routes/tickets"));
const inboxLazy = lazyWithRetryRoute(() => import("@/routes/inbox"));
const aiPhoneLazy = lazyWithRetryRoute(() => import("@/routes/ai-phone"));
const patientsLazy = lazyWithRetryRoute(() => import("@/routes/patients"));
const billingLazy = lazyWithRetryRoute(() => import("@/routes/billing"));
const settingsLazy = lazyWithRetryRoute(() => import("@/routes/settings"));
const waitingRoomLazy = lazyWithRetryRoute(() => import("@/routes/waiting-room"));
const receptionLazy = lazyWithRetryRoute(() => import("@/routes/reception"));
const tasksLazy = lazyWithRetryRoute(() => import("@/routes/tasks"));
const analyticsLazy = lazyWithRetryRoute(() => import("@/routes/analytics"));
const recallLazy = lazyWithRetryRoute(() => import("@/routes/recall"));
const followUpsLazy = lazyWithRetryRoute(() => import("@/routes/follow-ups"));
const triageLazy = lazyWithRetryRoute(() => import("@/routes/triage"));
const scheduleLazy = lazyWithRetryRoute(() => import("@/routes/schedule"));
const referralsLazy = lazyWithRetryRoute(() => import("@/routes/referrals"));
const careRequestsLazy = lazyWithRetryRoute(() => import("@/routes/care-requests"));
const eskalationenLazy = lazyWithRetryRoute(() => import("@/routes/eskalationen"));
const netzwerkLazy = lazyWithRetryRoute(() => import("@/routes/netzwerk"));
const consentsLazy = lazyWithRetryRoute(() => import("@/routes/consents"));
const surgicalConsentLazy = lazyWithRetryRoute(() => import("@/routes/surgical-consent"));
const videoConsultationLazy = lazyWithRetryRoute(() => import("@/routes/video-consultation"));
const videoCallLazy = lazyWithRetryRoute(() => import("@/routes/video-call"));
const recallResponseLazy = lazyWithRetryRoute(() => import("@/routes/recall-response"));
const visiteLazy = lazyWithRetryRoute(() => import("@/routes/visite"));
// MED-759: Gastseite der Praxis für eine Anfrage aus dem Pflegeheim (Link plus Einmalcode, ohne Konto).
const practiceRequestGuestLazy = lazyWithRetryRoute(() => import("@/routes/practice-request-guest"));
const visiteDokuLazy = lazyWithRetryRoute(() => import("@/routes/visite-doku"));
const visiteCockpitLazy = lazyWithRetryRoute(() => import("@/routes/visite-cockpit"));
const pendingApprovalsLazy = lazyWithRetryRoute(() => import("@/routes/visits/pending-approvals"));

function LegacyAIPhoneEntryRedirect() {
    const { callId } = useParams();

    return <Navigate to={buildAIPhonePath(callId)} replace />;
}

function AnalyticsAccessDeniedState() {
    return (
        <div className="flex min-h-[400px] items-center justify-center px-6 py-12">
            <div className="max-w-md rounded-2xl border border-neutral-200 bg-white p-8 text-center shadow-sm">
                <h2 className="text-lg font-semibold text-neutral-900">Kein Zugriff auf Analytics</h2>
                <p className="mt-3 text-sm leading-6 text-neutral-500">
                    Dieser Bereich ist nur für Praxisleitung und Administratoren freigeschaltet.
                </p>
            </div>
        </div>
    );
}

export const router = createBrowserRouter([
    {
        path: "/",
        lazy: landingLazy,
    },
    {
        path: "/login",
        element: <LoginPage />,
    },
    {
        path: "/accept-invite",
        lazy: acceptInviteLazy,
    },
    {
        path: "/preise",
        element: <PreisePage />,
    },
    {
        path: "/hilfe-center",
        element: <HilfeCenterPage />,
    },
    {
        path: "/impressum",
        element: <ImpressumPage />,
    },
    {
        path: "/datenschutz",
        element: <DatenschutzPage />,
    },
    {
        path: "/agb",
        element: <AGBPage />,
    },
    {
        path: "/avv",
        element: <AVVPage />,
    },
    {
        path: "/app",
        element: (
            <AuthGuard>
                <AppShell />
            </AuthGuard>
        ),
        children: [
            {
                index: true,
                lazy: dashboardLazy,
            },
            {
                path: "dashboard",
                lazy: dashboardLazy,
            },
            {
                path: "calendar",
                element: <FeatureRouteGuard featureKey="onlineBooking" />,
                children: [{ index: true, lazy: calendarLazy }],
            },
            {
                path: "tickets",
                lazy: ticketsLazy,
            },
            {
                path: "inbox",
                element: <FeatureRouteGuard featureKey="inAppMessaging" />,
                children: [{ index: true, lazy: inboxLazy }],
            },
            {
                path: "ai-phone",
                element: <FeatureRouteGuard featureKey="aiPhone" />,
                children: [{ index: true, lazy: aiPhoneLazy }],
            },
            {
                path: "ai-phone/:callId",
                element: <FeatureRouteGuard featureKey="aiPhone" />,
                children: [{ index: true, element: <LegacyAIPhoneEntryRedirect /> }],
            },
            {
                path: "video-consultation",
                element: <FeatureRouteGuard featureKey="videoConsultation" />,
                children: [{ index: true, lazy: videoConsultationLazy }],
            },
            {
                path: "patients",
                lazy: patientsLazy,
            },
            {
                path: "billing",
                element: <FeatureRouteGuard featureKey="billingGoae" />,
                children: [{ index: true, lazy: billingLazy }],
            },
            {
                path: "settings",
                lazy: settingsLazy,
            },
            {
                path: "visite-cockpit",
                lazy: visiteCockpitLazy,
            },
            {
                path: "visits/pending-approvals",
                lazy: pendingApprovalsLazy,
            },
            {
                path: "waiting-room",
                element: <FeatureRouteGuard featureKey="waitlistManagement" />,
                children: [{ index: true, lazy: waitingRoomLazy }],
            },
            {
                path: "reception",
                lazy: receptionLazy,
            },
            {
                path: "tasks",
                lazy: tasksLazy,
            },
            {
                path: "analytics",
                element: (
                    <FeatureRouteGuard featureKey="analyticsDashboard">
                        <RoleGuard
                            permission="analytics.read"
                            fallback={<AnalyticsAccessDeniedState />}
                        >
                            <Outlet />
                        </RoleGuard>
                    </FeatureRouteGuard>
                ),
                children: [{ index: true, lazy: analyticsLazy }],
            },
            {
                path: "recall",
                element: <FeatureRouteGuard featureKey="patientReminders" />,
                children: [{ index: true, lazy: recallLazy }],
            },
            {
                path: "follow-ups",
                element: <FeatureRouteGuard featureKey="patientReminders" />,
                children: [{ index: true, lazy: followUpsLazy }],
            },
            {
                path: "triage",
                element: <FeatureRouteGuard featureKey="aiTriage" />,
                children: [{ index: true, lazy: triageLazy }],
            },
            {
                path: "schedule",
                lazy: scheduleLazy,
            },
            {
                path: "referrals",
                lazy: referralsLazy,
            },
            {
                path: "care-requests",
                lazy: careRequestsLazy,
            },
            {
                // P-W34 Phase 3 — Eskalations-Inbox (Care → Praxis)
                path: "eskalationen",
                lazy: eskalationenLazy,
            },
            {
                path: "netzwerk",
                lazy: netzwerkLazy,
            },
            {
                path: "consents",
                element: <FeatureRouteGuard featureKey="documentSharing" />,
                children: [{ index: true, lazy: consentsLazy }],
            },
            {
                path: "surgical-consent",
                element: <FeatureRouteGuard featureKey="documentSharing" />,
                children: [{ index: true, lazy: surgicalConsentLazy }],
            },
        ],
    },
    {
        path: "/recall/respond/:recallId",
        lazy: recallResponseLazy,
    },
    {
        path: "/anfrage",
        lazy: practiceRequestGuestLazy,
    },
    {
        path: "/visite/:token/doku",
        lazy: visiteDokuLazy,
    },
    {
        path: "/visite/:token",
        lazy: visiteLazy,
    },
    {
        path: "/app/video-consultation/:sessionId/call",
        element: <AuthGuard />,
        children: [{ index: true, lazy: videoCallLazy }],
    },
    {
        // Unbekannte Pfade zeigen die 404-Seite statt still auf / umzuleiten —
        // der Redirect verschluckte Tippfehler und tote Links kommentarlos.
        path: "*",
        element: <NotFound />,
    },
]);

/**
 * Silicon Valley Standard: Intent-based Prefetching
 * Ruft den dynamischen Import der Route auf, BEVOR der User klickt (z.B. bei Mouse-Hover).
 * Der Browser lädt und speichert den JavaScript Chunk im Cache, was die Navigation instant macht.
 */
export function prefetchRoute(path: string) {
    // F-Pattern Hauptnavigation
    if (path.startsWith("/app/dashboard")) void dashboardLazy();
    else if (path.startsWith("/app/calendar")) void calendarLazy();
    else if (path.startsWith("/app/waiting-room")) void waitingRoomLazy();
    else if (path.startsWith("/app/reception")) void receptionLazy();
    // Kommunikation
    else if (path.startsWith("/app/inbox")) void inboxLazy();
    else if (path.startsWith("/app/tickets")) void ticketsLazy();
    else if (path.startsWith("/app/ai-phone")) void aiPhoneLazy();
    else if (path.startsWith("/app/video-consultation")) void videoConsultationLazy();
    else if (path.startsWith("/app/triage")) void triageLazy();
    // Daten & Verwaltung
    else if (path.startsWith("/app/patients")) void patientsLazy();
    else if (path.startsWith("/app/billing")) void billingLazy();
    else if (path.startsWith("/app/tasks")) void tasksLazy();
    else if (path.startsWith("/app/analytics")) void analyticsLazy();
    else if (path.startsWith("/app/recall")) void recallLazy();
    else if (path.startsWith("/app/follow-ups")) void followUpsLazy();
    else if (path.startsWith("/app/schedule")) void scheduleLazy();
    else if (path.startsWith("/app/referrals")) void referralsLazy();
    else if (path.startsWith("/app/care-requests")) void careRequestsLazy();
    else if (path.startsWith("/app/eskalationen")) void eskalationenLazy();
    else if (path.startsWith("/app/netzwerk")) void netzwerkLazy();
    else if (path.startsWith("/app/consents")) void consentsLazy();
    else if (path.startsWith("/app/surgical-consent")) void surgicalConsentLazy();
    else if (path.startsWith("/app/visits/pending-approvals")) void pendingApprovalsLazy();
    // Settings
    else if (path.startsWith("/app/settings")) void settingsLazy();
}
